import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useQuery } from 'react-apollo'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { path, head } from 'ramda'
import { format, toDate } from 'date-fns'

import { dataLayerPurchasePlan } from '../../../../lib/analytics/datalayer'
import { useCommonStyles } from '../../../shared/subscription-common.styles'
import { signupImage } from '../../../shared/subscription-constants'
import Button from '../../../../components/button'
import { LoadingModal } from '../../../../components/loading/modal'
import PlanDetails from '../../../../components/plan-details'
import VoucherMessages from '../../../../components/voucher-messages'
import { getModalReturnTarget } from '../../../../lib/modal'
import ACCOUNT_QUERY_WITH_DIR from '../../../../../graphql/queries/account-with-directives.gql'
import { usePlanChangeFlag } from '../../../../hooks/usePlanChange'
import { segmentTrackCompletePurchase, segmentTrackAddVoucher } from '../../../../segment/segment-track'
import { getGQLErrorMsg } from '../../../../lib/apollo'
import styles from './add-voucher-confirmation.css'

const EMPTY_DETAILS = {
  productName: null,
  productPrice: null,
  nextPaymentDate: null,
  chargedPrice: null
}

/**
 * AddVoucherConfirmation
 *
 * Final step of the add-voucher flow. It fetches refreshed subscription data,
 * builds confirmation plan/payment details, fires segment analytics, and
 * renders the success state with a CTA to return to the modal target page.
 */
const AddVoucherConfirmation = ({
  orderId,
  onError
}) => {
  const commonStyles = useCommonStyles()
  const history = useHistory()
  const location = useLocation()
  const [details, setDetails] = useState()
  const getPlanDetails = usePlanChangeFlag()

  const onClick = () => {
    history.push(getModalReturnTarget(location))
  }

  // Fetch latest subscription data and clear cached subscriptions in Apollo.
  const {
    data: accountQueryData,
    loading: accountQueryLoading,
    error: accountQueryError
  } = useQuery(ACCOUNT_QUERY_WITH_DIR, {
    fetchPolicy: 'network-only',
    variables: {
      withSubscription: true,
      withCreditcards: true,
      withClearCachedSubscriptions: true
    }
  })

  const getSubscriptionStaticDetail = (sku) => {
    const subscriptions = [
      getPlanDetails.basic,
      getPlanDetails.standard,
      getPlanDetails.annual,
      getPlanDetails.legacy
    ]

    const matchedSubscription = subscriptions.find(sub => sub.sku === sku)
    return matchedSubscription?.staticDetail || { name: null, value: null }
  }

  // Build confirmation details and fire purchase analytics for successful voucher redemption.
  const getConfirmationDetails = () => {
    const vouchers = path(
      ['account', 'subscription', 'recurringVouchers'],
      accountQueryData
    )
    const voucher = head(vouchers)
    const nextPaymentDate = path(['orderDetails', 'nextPaymentDate'], voucher)
    const chargedPrice = path(['orderDetails', 'chargedPrice'], voucher)
    const sku = path(['orderDetails', 'sku'], voucher)

    if (!nextPaymentDate || !sku) {
      return EMPTY_DETAILS
    }

    const nextPaymentDateFormatted = format(
      toDate(parseInt(nextPaymentDate, 10)),
      'd MMM yyyy'
    )

    const subscriptionStaticDetail = getSubscriptionStaticDetail(sku)

    // Add segment data analytics for purchase completion after adding voucher
    segmentTrackCompletePurchase({
      planTitle: subscriptionStaticDetail.name,
      planSku: sku,
      paymentDue: nextPaymentDateFormatted,
      payment: Number(chargedPrice),
      voucher,
      orderId
    })

    // Add segment data analytics for adding voucher
    segmentTrackAddVoucher({
      email: accountQueryData.account.email,
      orderId,
      voucher
    })

    dataLayerPurchasePlan(accountQueryData.account, orderId)

    return {
      productName: subscriptionStaticDetail.name,
      productPrice: subscriptionStaticDetail.value,
      nextPaymentDate: nextPaymentDateFormatted,
      chargedPrice
    }
  }

  // Handle query outcomes:
  // - route back with error message when account query fails
  // - set confirmation details when data is available
  useEffect(() => {
    if (accountQueryError) {
      onError(getGQLErrorMsg(accountQueryError))
      return
    }

    if (accountQueryLoading || !accountQueryData) {
      return
    }

    const {
      productName, productPrice, nextPaymentDate, chargedPrice
    } = getConfirmationDetails()

    if (nextPaymentDate && chargedPrice) {
      setDetails([
        {
          name: `${productName}`,
          value: `${productPrice}`
        },
        {
          name: 'First payment due',
          value: `${nextPaymentDate}`
        },
        {
          name: 'First payment',
          value: `$${chargedPrice}`
        }
      ])
    }
  }, [accountQueryData, accountQueryLoading, accountQueryError, orderId])

  if (accountQueryLoading || !accountQueryData) {
    return <LoadingModal />
  }

  return (
    <div className={classNames(styles.wrapper)}>
      <div className={styles.header}>Your voucher is good to go!</div>
      {details && <PlanDetails detail={details} showVoucherMessage />}
      <VoucherMessages />
      <img
        src={signupImage}
        alt="Plan confirmation"
        className={commonStyles.image}
      />
      <Button className={styles.button} text="Get started!" onClick={onClick} />
    </div>
  )
}

AddVoucherConfirmation.propTypes = {
  orderId: PropTypes.string.isRequired,
  onError: PropTypes.func.isRequired
}

export default AddVoucherConfirmation
